import React from 'react';

import Dropdown from '$/components/ui/Input/Basics/Dropdown';
import Input from '$/components/ui/Input/Basics/Input';
import CountryInput from '$/components/ui/Input/Location/CountryInput';
import ProvinceInput from '$/components/ui/Input/Location/ProvinceInput';
import StateInput from '$/components/ui/Input/Location/StateInput';
import PhoneNumber from '$/components/ui/Input/PhoneNumber';
import { RegistrationFieldType } from '$/GraphQL/schema/schema';
import { I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { FieldTypeOption } from './RegistrationForm.types';

export const useFieldTypeOptions = (
  country: string,
  i18n: I18n
): (FieldTypeOption & { desc?: string })[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let ProvinceControl: any = null;
  let clearable = false;
  switch (country.toUpperCase()) {
    case 'US':
      ProvinceControl = StateInput;
      clearable = true;
      break;
    case 'CA':
      clearable = true;
      ProvinceControl = ProvinceInput;
      break;
    default:
      ProvinceControl = Input;
      break;
  }

  const fieldTypeOptions: (FieldTypeOption & { desc?: string })[] = React.useMemo(() => {
    let placeholder = '';

    switch (country.toUpperCase()) {
      case 'US':
        placeholder = t(i18n)`Select State`;
        break;
      case 'CA':
        placeholder = t(i18n)`Select Province`;
        break;
      default:
        placeholder = t(i18n)`Enter Province`;
        break;
    }

    return [
      {
        value: RegistrationFieldType.Text,
        text: RegistrationFieldType.Text,
        desc: 'A simple text field.',
        component: <Input />,
      },
      {
        value: RegistrationFieldType.Email,
        text: RegistrationFieldType.Email,
        desc: 'Field expects a valid email input. Every form already has 1 required email field.',
        component: <Input />,
      },
      {
        value: RegistrationFieldType.Phone,
        text: RegistrationFieldType.Phone,
        desc: 'Smart phone number capture field.',
        component: <PhoneNumber country={country} />,
      },
      {
        value: RegistrationFieldType.Countries,
        text: 'Country selector',
        desc: 'A dropdown that offers a searchable dropdown of all countries.',
        component: (
          <CountryInput clearable autoComplete='country-name' placeholder={t(i18n)`Your country`} />
        ),
      },
      {
        value: RegistrationFieldType.States,
        text: 'State or Province selector',
        desc: `This field automatically links to a country selector in the form.  If the country chosen is the United States this is a dropdown of US States, otherwise it's an simple text field.`,
        component: (
          <ProvinceControl
            clearable={clearable}
            placeholder={placeholder}
            autoComplete='address-level1'
          />
        ),
      },
      {
        value: RegistrationFieldType.Selection,
        text: 'Custom selector',
        desc: 'Define your own single or multi-select dropdown',
        component: <Dropdown clearable selection upward fluid placeholder={t(i18n)`Select item`} />,
      },
      {
        value: RegistrationFieldType.Hidden,
        text: RegistrationFieldType.Hidden,
        desc: `Hidden fields aren't seen by registrants.  They are useful as they can be filled in automatically via URL parameters such as <registration page>?customerID=1234`,
        component: <Input />,
      },
    ];
  }, [ProvinceControl, clearable, country, i18n]);

  return fieldTypeOptions;
};
